import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import axios from 'axios';
import { Navigation, MessageSquare, MapPin, Pencil, Package, Weight, Image as ImageIcon, Calendar } from 'lucide-react';
import MissionListCard from '@/components/MissionListCard';
import { cityFlag as flag } from '@/utils/cityFlag';
import { ccySym } from '@/utils/currencyUtils';
import '@/styles/missions-page.css';
import { BACKEND_URL } from '@/config';
import { photoCount } from '@/utils/photos';

const API = BACKEND_URL + '/api';
axios.defaults.withCredentials = true;

const STATUS = {
  PENDING:         { label: 'En attente',     tone: 'gray' },
  ACCEPTED:        { label: 'Accepté',        tone: 'teal' },
  PAYMENT_PENDING: { label: 'Paiement',       tone: 'gold' },
  IN_ESCROW:       { label: 'En séquestre',   tone: 'gold' },
  IN_TRANSIT:      { label: 'En transit',     tone: 'gold' },
  DELIVERED:       { label: 'Livré',          tone: 'blue' },
  FUNDS_RELEASED:  { label: 'Fonds libérés',  tone: 'teal' },
  COMPLETED:       { label: 'Terminé',        tone: 'teal' },
  DISPUTED:        { label: 'Litige',         tone: 'red' },
  CANCELLED:       { label: 'Annulé',         tone: 'red' },
};

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

const FILTERS = [
  ['all',        'Toutes'],
  ['PENDING',    'En attente'],
  ['ACCEPTED',   'Acceptées'],
  ['IN_TRANSIT', 'En transit'],
  ['DELIVERED',  'Livrées'],
  ['COMPLETED',  'Terminées'],
  ['DISPUTED',   'Litiges'],
  ['CANCELLED',  'Annulées'],
];

// Sélecteur de rôle réutilisable
function RoleSelector({ roleFilter, onChange, carrierCount, senderCount }) {
  const opts = [
    {
      key: 'as_sender',
      icon: <Package size={14} />,
      label: 'Expéditeur',
      color: '#0e7a68',
      bg: '#ecfdf5',
      count: senderCount,
    },
    {
      key: 'as_carrier',
      icon: <Navigation size={14} />,
      label: 'Transporteur',
      color: '#C8832A',
      bg: '#fef9f0',
      count: carrierCount,
    },
  ];

  return (
    <div className="ms-role-row">
      {opts.map(o => {
        const active = roleFilter === o.key;
        return (
          <button
            key={o.key}
            type="button"
            data-testid={o.key === 'as_sender' ? 'role-btn-sender' : 'role-btn-carrier'}
            onClick={() => onChange(o.key)}
            className={`ms-role-btn ms-role-btn--${o.key.replace('as_','')}${active ? ' active' : ''}`}
          >
            {o.icon}
            {o.label}
            {o.count > 0 && (
              <span className={`ms-role-count${active ? ' active' : ''}`}>
                {o.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default function MissionsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const isSender  = user?.roles?.includes('ROLE_SENDER');
  const isCarrier = user?.roles?.includes('ROLE_CARRIER');
  const isHybrid  = isSender && isCarrier;

  const defaultRole = user?.active_role === 'ROLE_CARRIER' ? 'as_carrier' : 'as_sender';
  const [roleFilter, setRoleFilter] = useState(defaultRole);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const params = filter !== 'all' ? { status: filter } : {};
    axios.get(`${API}/missions`, { params })
      .then(({ data }) => { if (!cancel) setMissions(data.data || []); })
      .catch(() => toast.error('Erreur de chargement'))
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [filter, toast]);

  // Filtrage client-side pour les hybrides
  const filteredMissions = useMemo(() => {
    if (!isHybrid) return missions;
    const uid = user?.id;
    return missions.filter(m =>
      roleFilter === 'as_carrier' ? m.carrier_id === uid : m.shipper_id === uid
    );
  }, [missions, isHybrid, roleFilter, user]);

  const carrierCount = useMemo(() => {
    if (!isHybrid) return 0;
    const uid = user?.id;
    return missions.filter(m => m.carrier_id === uid).length;
  }, [missions, isHybrid, user]);

  const senderCount = useMemo(() => {
    if (!isHybrid) return 0;
    const uid = user?.id;
    return missions.filter(m => m.shipper_id === uid).length;
  }, [missions, isHybrid, user]);

  const stats = useMemo(() => {
    const list = isHybrid ? filteredMissions : missions;
    return {
      total:  list.length,
      active: list.filter(m => ['PENDING','ACCEPTED','PAYMENT_PENDING','IN_ESCROW','IN_TRANSIT','DELIVERED'].includes(m.status)).length,
    };
  }, [filteredMissions, missions, isHybrid]);

  const buildChips = (m) => {
    const ship = m.shipment || {};
    const out = [];
    if (ship.weight_kg) out.push({ icon: Weight, label: `${ship.weight_kg} kg` });
    if (photoCount(ship)) out.push({ icon: ImageIcon, label: `${photoCount(ship)} photo${photoCount(ship) > 1 ? 's' : ''}` });
    if (ship.departure_date) out.push({ icon: Calendar, label: formatDate(ship.departure_date) });
    return out;
  };

  const buildActions = (m) => {
    const acts = [];
    acts.push({
      key: 'msg',
      icon: <MessageSquare size={16} />,
      label: 'Messagerie',
      testid: `mc-msg-${m.id}`,
      onClick: () => navigate(`/app/messages${m.conversation_id ? `?c=${m.conversation_id}` : ''}`),
    });
    if (['IN_TRANSIT', 'IN_ESCROW', 'DELIVERED'].includes(m.status)) {
      acts.push({
        key: 'track',
        icon: <MapPin size={16} />,
        label: 'Suivi',
        testid: `mc-track-${m.id}`,
        onClick: () => navigate(`/app/missions/${m.id}`),
      });
    } else if (['PENDING', 'ACCEPTED'].includes(m.status)) {
      acts.push({
        key: 'edit',
        icon: <Pencil size={16} />,
        label: 'Détails',
        testid: `mc-edit-${m.id}`,
        onClick: () => navigate(`/app/missions/${m.id}`),
      });
    }
    return acts;
  };

  const roleLabel = roleFilter === 'as_carrier' ? 'Transporteur' : 'Expéditeur';

  return (
    <div>
      <div className="c2s-topbar">
        <div>
          <div className="c2s-topbar-title">Mes missions</div>
          <div className="c2s-topbar-sub">
            {stats.total} mission{stats.total > 1 ? 's' : ''} · {stats.active} active{stats.active > 1 ? 's' : ''}
            {isHybrid && <> · en tant que <strong>{roleLabel}</strong></>}
          </div>
        </div>
      </div>

      {/* Sélecteur de rôle (hybrides uniquement) */}
      {isHybrid && (
        <RoleSelector
          roleFilter={roleFilter}
          onChange={setRoleFilter}
          carrierCount={carrierCount}
          senderCount={senderCount}
        />
      )}

      {/* Filtres statut */}
      <div className="mlc-filters" data-testid="missions-filters">
        {FILTERS.map(([v, l]) => (
          <button key={v}
            type="button"
            data-testid={`filter-missions-${v}`}
            className={`mlc-filter${filter === v ? ' on' : ''}`}
            onClick={() => setFilter(v)}>
            {l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mlc-list">
          {[1,2,3].map(i => <div key={i} className="skeleton" style={{ height: 168, borderRadius: 14 }} />)}
        </div>
      ) : filteredMissions.length === 0 ? (
        <div className="empty-state">
          <Navigation size={48} className="empty-state-icon" />
          <div className="empty-state-title">Aucune mission</div>
          <div className="empty-state-sub">
            {filter === 'all'
              ? isHybrid
                ? `Aucune mission en tant que ${roleLabel}.`
                : 'Créez une annonce et trouvez un transporteur pour démarrer.'
              : `Aucune mission avec ce statut.`}
          </div>
        </div>
      ) : (
        <div className="mlc-list" data-testid="missions-list">
          {filteredMissions.map(m => {
            const ship = m.shipment || {};
            const fromCity = ship.departure_city || '?';
            const toCity = ship.arrival_city || '?';
            const ref = m.tracking_code ? `#${m.tracking_code}` : `#MSN-${(m.id || '').slice(-6).toUpperCase()}`;
            const sublineParts = [formatDate(m.created_at), ref];
            if (photoCount(ship)) sublineParts.push(`${photoCount(ship)} colis`);

            return (
              <MissionListCard
                key={m.id}
                testid={`mission-card-${m.id}`}
                route={<>{flag(fromCity)} {fromCity} → {flag(toCity)} {toCity}</>}
                subline={sublineParts.join(' · ')}
                status={STATUS[m.status] || { label: m.status, tone: 'gray' }}
                chips={buildChips(m)}
                amount={Number(m.contracted_price || 0).toFixed(0)}
                amountUnit={ccySym(m.currency || 'EUR')}
                actions={buildActions(m)}
                onClick={() => navigate(`/app/missions/${m.id}`)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
